import { Hearing } from '@enview/interface/types/bills/Bill';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ActionCalendar from '../../../../components/svg/CalendarCircleIcon';
import Info from '../../../../components/svg/InfoCircleIcon';
import { ActivityCard, ActivityCardStyle } from './ActivityCard';
import './ActivityCard.scss';

type HearingActivityCardProps = {
  hearing: Hearing;
};

const HearingActivityCard = (props: HearingActivityCardProps): ReactElement => {
  const { hearing } = props;
  const scheduledDate = new Date(hearing.start);
  const today = new Date();
  const pretitle = scheduledDate < today ? 'Past Hearing' : 'Hearing Scheduled';
  const { t } = useTranslation();

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  };

  const { location, sources, agenda } = hearing.eventData;
  const sourceLink = sources && sources.length ? sources[0].url : undefined;

  const attachments: string[] = [];
  agenda.forEach((agendaItem) => {
    agendaItem.media.forEach((mediaItem) => attachments.push(mediaItem));
  });

  const description =
    agenda.length > 0
      ? agenda[agenda.length - 1].description
      : hearing.eventData.description;
  const truncatedDescription =
    description.length > 150 ? (
      <p id="description">
        {description.substring(0, 150)}{' '}
        <a href={sourceLink} target="_new">
          [...]
        </a>
      </p>
    ) : (
      ''
    );

  const getSubtitle = (): ReactElement => {
    const formattedDate = scheduledDate.toLocaleDateString('en-US', dateOptions);
    return (
      <>
        <span>{formattedDate}</span>
        <span
          className="ml-1"
          data-tooltip-id={`hearing-event-tooltip-detail-${hearing.id}`}
        >
          <Info />
        </span>
        <ReactTooltip
          children={t('activity.seeSourceMessage')}
          className="tooltip-content"
          clickable
          delayHide={200}
          id={`hearing-event-tooltip-detail-${hearing.id}`}
          place="bottom"
          style={{ maxWidth: '300px' }}
        />
      </>
    );
  };

  const expandedContent = (
    <>
      {location.name && (
        <div className="expandable-content-detail">
          <div className="expanded-header">Location</div>
          <p>{location.name}</p>
        </div>
      )}
      {description && (
        <div className="expandable-content-detail">
          <div className="expanded-header">Description</div>
          {truncatedDescription}
        </div>
      )}
      {location.url && (
        <div className="expandable-content-detail">
          <div className="expanded-header">Links</div>
          <a href={location.url}>{location.url}</a>
        </div>
      )}
      {attachments && attachments.length > 0 && (
        <div className="expandable-content-detail">
          <div className="expanded-header">Attachments</div>
          {attachments.map((attachment) => (
            <a href={attachment} key={attachment} target="_new">
              {attachment}
            </a>
          ))}
        </div>
      )}
      {sourceLink && (
        <a className="source-link" href={sourceLink} target="_new">
          Source Link
        </a>
      )}
    </>
  );

  return (
    <ActivityCard
      activityStyle={ActivityCardStyle.BILL_HEARING}
      expandedContent={expandedContent}
      icon={<ActionCalendar />}
      iconColor="purple"
      isExpandable
      pretitle={pretitle}
      subtitle={getSubtitle()}
      title={<span>{hearing.eventData.name}</span>}
    />
  );
};

export default HearingActivityCard;
